/* eslint-disable max-lines */
import {
  AggregationsDialog,
  ChartSettingsDialog,
  ChartDataDialog,
  ChartActionDialog,
  ChartFullscreenDialog,
  ExportAnalysisDialog,
} from '@store/types';
import {
  attributeSettingsDefaultValues,
  defaultAttributeCompetitor,
  dialogDefaultState,
  EMPTY_STRING,
  STEP,
  valueMapSettingsDefaultValues,
} from '@constants';
import {
  Aggregation,
  AggregationSummaryItem,
  AttributeSettingsForm,
  ChartExportForm,
  ChartsData,
  CompareAggregation,
  GeneralAnalysisAnswer,
  GetValueComparison,
  Nullable,
  Option,
  ValueMapSettingsForm,
  WordCloudAnalyse,
} from '@customTypes';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectedBenchmark, CategoryDialog } from '@store/types';
import { EChartsOption } from 'echarts-for-react';

export interface ProjectAnalysisState {
  isTextAnalysisActive: boolean;
  isTextAnalysisLoading: boolean;
  generalAnswers: GeneralAnalysisAnswer[];
  wordCloudAnalyse: WordCloudAnalyse;
  chartsData: Nullable<ChartsData>;
  aggregationsDialog: AggregationsDialog;
  selectedBenchmark: Nullable<SelectedBenchmark>;
  categoryDialog: CategoryDialog;
  aggregations: Aggregation[];
  categories: Option[];
  activeSimpleTab: number;
  isCompletenessDialogOpen: boolean;
  aggregationSummaryItems: AggregationSummaryItem[];
  chartSettingsDialog: ChartSettingsDialog;
  valueMapSettings: ValueMapSettingsForm;
  attributeSettings: AttributeSettingsForm;
  chartDataDialog: ChartDataDialog;
  chartExportSettings: Nullable<ChartExportForm>;
  selectedChartOptions: Nullable<EChartsOption>;
  chartExportDialog: ChartActionDialog;
  chartFullscreenDialog: ChartFullscreenDialog;
  attributeCompetitor: string;
  compareAggregationPayload: GetValueComparison['Request'];
  compareAggregation: Nullable<Record<string, CompareAggregation>>;
  exportAnalysisDataDialog: ExportAnalysisDialog;
  initialAttributeSettings: AttributeSettingsForm;
}

const initialState: ProjectAnalysisState = {
  isTextAnalysisActive: false,
  isTextAnalysisLoading: false,
  generalAnswers: [],
  wordCloudAnalyse: {},
  chartsData: null,
  aggregationsDialog: {
    ...dialogDefaultState,
    isCancelDialogOpen: false,
  },
  selectedBenchmark: null,
  categoryDialog: dialogDefaultState,
  aggregations: [],
  categories: [],
  activeSimpleTab: STEP,
  isCompletenessDialogOpen: false,
  aggregationSummaryItems: [],
  chartSettingsDialog: dialogDefaultState,
  valueMapSettings: valueMapSettingsDefaultValues,
  attributeSettings: attributeSettingsDefaultValues,
  chartDataDialog: {
    ...dialogDefaultState,
    headers: [],
    data: [],
    title: EMPTY_STRING,
  },
  chartExportSettings: null,
  selectedChartOptions: null,
  chartExportDialog: dialogDefaultState,
  chartFullscreenDialog: dialogDefaultState,
  attributeCompetitor: defaultAttributeCompetitor,
  compareAggregationPayload: {},
  compareAggregation: null,
  exportAnalysisDataDialog: dialogDefaultState,
  initialAttributeSettings: attributeSettingsDefaultValues,
};

export const projectAnalysisSlice = createSlice({
  name: 'projectAnalysis',
  initialState,
  reducers: {
    setTextAnalysisActive: (state, action: PayloadAction<boolean>) => {
      state.isTextAnalysisActive = action.payload;
    },
    setTextAnalysisLoading: (state, action: PayloadAction<boolean>) => {
      state.isTextAnalysisLoading = action.payload;
    },
    setGeneralAnswers: (state, action: PayloadAction<GeneralAnalysisAnswer[]>) => {
      state.generalAnswers = action.payload;
    },
    setWordCloudAnalyse: (state, action: PayloadAction<WordCloudAnalyse>) => {
      state.wordCloudAnalyse = action.payload;
    },
    setChartsData: (state, action: PayloadAction<ChartsData>) => {
      state.chartsData = action.payload;
    },
    setAggregationDialog: (state, action: PayloadAction<AggregationsDialog>) => {
      state.aggregationsDialog = action.payload;
    },
    setSelectedBenchmark: (state, action: PayloadAction<SelectedBenchmark>) => {
      state.selectedBenchmark = action.payload;
    },
    setCategoryDialog: (state, action: PayloadAction<CategoryDialog>) => {
      state.categoryDialog = action.payload;
    },
    setAggregations: (state, action: PayloadAction<Aggregation[]>) => {
      state.aggregations = action.payload;
    },
    setCategories: (state, action: PayloadAction<Option[]>) => {
      state.categories = action.payload;
    },
    setActiveSimpleTab: (state, action: PayloadAction<number>) => {
      state.activeSimpleTab = action.payload;
    },
    setCompletenessDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isCompletenessDialogOpen = action.payload;
    },
    resetAnalysisDialogsState: (state) => {
      state.isCompletenessDialogOpen = initialState.isCompletenessDialogOpen;
      state.categoryDialog = initialState.categoryDialog;
      state.aggregationsDialog = initialState.aggregationsDialog;
      state.aggregationSummaryItems = initialState.aggregationSummaryItems;
      state.chartDataDialog = initialState.chartDataDialog;
      state.chartExportDialog = initialState.chartExportDialog;
      state.attributeSettings = initialState.attributeSettings;
      state.valueMapSettings = initialState.valueMapSettings;
    },
    setAggregationSummaryItems: (state, action: PayloadAction<AggregationSummaryItem[]>) => {
      state.aggregationSummaryItems = action.payload;
    },
    setChartSettingsDialog: (state, action: PayloadAction<ChartSettingsDialog>) => {
      state.chartSettingsDialog = action.payload;
    },
    setValueMapSettings: (state, action: PayloadAction<ValueMapSettingsForm>) => {
      state.valueMapSettings = action.payload;
    },
    setAttributeSettings: (state, action: PayloadAction<AttributeSettingsForm>) => {
      state.attributeSettings = action.payload;
    },
    resetChartSettingsData: (state) => {
      state.attributeSettings = initialState.attributeSettings;
      state.valueMapSettings = initialState.valueMapSettings;
    },
    setChartDataDialog: (state, action: PayloadAction<ChartDataDialog>) => {
      state.chartDataDialog = action.payload;
    },
    resetChartDataDialog: (state) => {
      state.chartDataDialog = initialState.chartDataDialog;
    },
    setChartExportDialog: (state, action: PayloadAction<ChartActionDialog>) => {
      state.chartExportDialog = action.payload;
    },
    setChartExportSettings: (state, action: PayloadAction<ChartExportForm>) => {
      state.chartExportSettings = action.payload;
    },
    setSelectedChartOptions: (state, action: PayloadAction<EChartsOption>) => {
      state.selectedChartOptions = action.payload;
    },
    setChartFullscreenDialog: (state, action: PayloadAction<ChartFullscreenDialog>) => {
      state.chartFullscreenDialog = action.payload;
    },
    setAttributeCompetitor: (state, action: PayloadAction<string>) => {
      state.attributeCompetitor = action.payload;
    },
    setCompareAggregationPayload: (state, action: PayloadAction<GetValueComparison['Request']>) => {
      state.compareAggregationPayload = action.payload;
    },
    setCompareAggregation: (state, action: PayloadAction<Record<string, CompareAggregation>>) => {
      state.compareAggregation = action.payload;
    },
    setExportAnalysisDialog: (state, action: PayloadAction<ExportAnalysisDialog>) => {
      state.exportAnalysisDataDialog = action.payload;
    },
    resetCategories: (state) => {
      state.categories = initialState.categories;
    },
    setInitialAttributeSettings: (state, action: PayloadAction<AttributeSettingsForm>) => {
      state.initialAttributeSettings = action.payload;
    },
  },
});

export const {
  setTextAnalysisActive,
  setTextAnalysisLoading,
  setGeneralAnswers,
  setAggregationDialog,
  setSelectedBenchmark,
  setWordCloudAnalyse,
  setChartsData,
  setCategoryDialog,
  setAggregations,
  setCategories,
  setActiveSimpleTab,
  resetAnalysisDialogsState,
  setCompletenessDialogOpen,
  setAggregationSummaryItems,
  setChartSettingsDialog,
  setValueMapSettings,
  setAttributeSettings,
  resetChartSettingsData,
  setChartDataDialog,
  resetChartDataDialog,
  setChartExportDialog,
  setChartExportSettings,
  setSelectedChartOptions,
  setChartFullscreenDialog,
  setAttributeCompetitor,
  setCompareAggregationPayload,
  setCompareAggregation,
  setExportAnalysisDialog,
  resetCategories,
  setInitialAttributeSettings,
} = projectAnalysisSlice.actions;
export default projectAnalysisSlice.reducer;
